<template>
  <div class="relative flex h-full">
    <DashboardSidePanel
      @select-project="selectProject"
      v-model:addingNewProject="addingNewProject"
      :selectedProject="selectedProject"
      :projects="projects"
    />
    <ProjectPanel
      @project-added="refreshProjects(projects.length)"
      @project-unsynced="refreshProjects(projects.length - 2)"
      @survey-saved="refreshProjects(selectedProjectIdx)"
      @survey-deleted="refreshProjects(selectedProjectIdx)"
      :addingNewProject="addingNewProject"
      :selectedProject="selectedProject"
      :redcapApiTokens="redcapApiTokens"
      :projects="projects"
      :selectedRedcapProjectInfo="selectedRedcapProjectInfo"
      :selectedRedcapToken="selectedRedcapToken"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed, inject } from "vue"

import DashboardSidePanel from "../components/DashboardSidePanel.vue"
import ProjectPanel from "../components/ProjectPanel.vue"
import ApiClient, { RedcapApiTokenApi, ProjectApi } from "../api"

const user = inject("user")
const redcapApiTokens = ref([])
const projects = computed(() => redcapApiTokens.value.map(t => t.project))
const redcapProjectsInfo = ref([])
const selectedProject = ref(null)
const selectedRedcapProjectInfo = ref(null)
const selectedRedcapToken = ref(null)

async function fetchAll() {
  if (user.value) {
    await fetchRedcapApiTokens()
    // await fetchSurveys()
    for (const token of redcapApiTokens.value) {
      const details = (token?.project?.id)
        ? await updateProjectInfo(token)
        : {}
      redcapProjectsInfo.value.push(details)
    }
  }
}

async function fetchRedcapApiTokens() {
  redcapApiTokens.value = (
    await RedcapApiTokenApi.all({
      where: { userId: user.value.id },
      // current back end support for this "includes" syntax is not complete, but works for this case
      // the incomplete back-end request causes the keys within substanceMode (5 models deep) to be truncated
      // things working now, but this is wonky -> see dataTyp key used in SurveyDetails
      include: [
        {
          project: {
            otlfbSurvey: {
              otlfbSurveySubstances: ["substance", "substanceMode"]
            }
          }
        }
      ]
    })
  ).data

  redcapApiTokens.value.forEach(token => {
    //sort the otlfbSurveySubstances by id
    if (token.project && token.project.otlfbSurvey) {
      token.project.otlfbSurvey.otlfbSurveySubstances.sort((a, b) => a.id - b.id)
    }
  })
}

async function updateProjectInfo(token) {
  
  const response = await ApiClient.post(`/export`, {
    content: "projectinfo",
    projectId: token.project.id,
  })

  if (response.data.project_title !== token.project.name) {
    const projectUpdateResponse = await ProjectApi.update({
      id: token.project.id,
      name: response.data.project_title
    })
    await fetchRedcapApiTokens()
  }

  return response.data
}

onMounted(async () => {
  await fetchAll()
  if (projects.value.length > 0) {
    selectProject(0)
  }
})

watch(user, fetchAll)

const addingNewProject = ref(true)
watch(addingNewProject, () => {
  if (addingNewProject.value) {
    selectedProject.value = null
    selectedRedcapProjectInfo.value = null
    selectedRedcapToken.value = null
  }
})

async function selectProject(idx) {
  selectedProject.value = projects.value[idx]
  selectedRedcapProjectInfo.value = redcapProjectsInfo.value[idx]
  selectedRedcapToken.value = redcapApiTokens.value[idx]
  addingNewProject.value = false
}

async function refreshProjects(projectIndex) {
  redcapApiTokens.value = []
  redcapProjectsInfo.value = []
  
  await fetchAll()
  if (projects.value.length) {
    if (projectIndex > (projects.value.length - 1)) {
      selectProject(projects.value.length - 1)
    } else {
      selectProject(projectIndex)
    }
  } else {
    addingNewProject.value = true
  }
}

const selectedProjectIdx = computed(() => {
  if (!selectedProject.value) return null
  return projects.value.map(p => p.id).indexOf(selectedProject.value.id)
})
</script>
